/**
 * The single error shape every endpoint in this contract returns on a non-2xx
 * response.
 */
import { z } from 'zod';
import { CorrelationIdSchema } from './primitives.js';
/**
 * Error codes.
 *
 * ## Why `not_found` and not `forbidden` for out-of-scope reads
 *
 * NFR-06 puts scope in the query predicate, so an out-of-scope contract is not
 * *refused* — it is never retrieved. The API cannot distinguish "does not exist"
 * from "exists but outside your scope", and it must not: `403` on an out-of-scope
 * key would confirm the contract's existence to someone not entitled to know it,
 * which is an existence oracle over cloudEQ's client list.
 *
 * PR-10's *"rejects unauthenticated and unscoped requests uniformly"* is the same
 * rule stated from the other side. `forbidden` is therefore reserved for a
 * failure about the **caller**, never about a resource: a role that does not
 * permit an action the caller named explicitly — a PM attempting a clause
 * master-text edit reserved to Legal under FR-17 — or a sign-in that resolves to
 * no CLM role or no CLM scope at all. No existence information leaks from
 * either; `reason` ({@link API_DENIAL_REASONS}) says which it was.
 */
export const API_ERROR_CODES = [
    /** No token, expired token, or a token this API does not accept. */
    'unauthenticated',
    /**
     * The caller may not take this action — their role, scope or the handler does
     * not permit it, or their sign-in resolves to no CLM role or no CLM scope at
     * all. `reason` says which. Never used for an out-of-scope *resource*: that
     * is `not_found`.
     */
    'forbidden',
    /** Absent, or outside the caller's resolved scope. Indistinguishable by design. */
    'not_found',
    /** The request failed schema validation. See `field_errors`. */
    'validation_failed',
    /** Lost an optimistic-concurrency race, or violated a uniqueness rule. */
    'conflict',
    /** §8.1 Rule 3 — a `Superseded` contract is read-only. */
    'superseded_read_only',
    /** §4C — the action would send a `processing_restricted` contract to an LLM. */
    'processing_restricted',
    /**
     * §8.1 Rule 1 (D-124-1) — an amendment may only target an `Executed` or `Active` agreement.
     * Declared, never emitted: the upload refuses as `relationship_not_valid` (R-124-4).
     */
    'amendment_target_not_active',
    /** Payload larger than the accepted maximum, or an unsupported media type. */
    'unsupported_document',
    'rate_limited',
    'internal',
];
export const ApiErrorCodeSchema = z.enum(API_ERROR_CODES);
/**
 * Why a `forbidden` was returned — about the **caller's own sign-in**, never
 * about a resource.
 *
 * ## Why this is on the wire at all
 *
 * Until 30 Sep 2026 the reason lived only in the gateway log. That day a member
 * of `CEQ-CLM-Admin` who held no `CEQ-CLM-Scope-*` group got a `403` on every
 * call, and the SPA — which had nothing but `code: 'forbidden'` to go on — told
 * her *"Your role may not take this action"*. Her role was fine; the missing
 * piece was a scope group. The log knew which, and nobody reading the page could.
 *
 * Saying which of these applies discloses nothing the caller does not already
 * hold: each is a statement about the groups in the caller's **own verified
 * token**, reached only after authentication succeeded (an anonymous caller
 * still gets the one uniform `401`). None of them names a contract, a party or
 * a group id, and none varies with whether any row exists — so the `not_found`
 * rule above is untouched: an out-of-scope key is still a `404` with no
 * `reason`, never a `403`.
 *
 * - `no_clm_role_group` — authenticated, holds none of the CLM role groups.
 * - `no_clm_scope_group` — holds a role, but no scope group, so nothing is in
 *   scope.
 * - `no_groups_claim` — the token carried no `groups` claim at all (no groups,
 *   or the app registration does not emit them).
 * - `action_not_permitted` — authorisation resolved; this caller may not take
 *   this action here. That is the role gate, a Scope-All requirement (a
 *   practice-scoped caller writing the verification policy), or a handler's own
 *   refusal such as cancelling another person's run. So the cause is not always
 *   the role. The original meaning of `forbidden`.
 */
export const API_DENIAL_REASONS = [
    'no_clm_role_group',
    'no_clm_scope_group',
    'no_groups_claim',
    'action_not_permitted',
];
export const ApiDenialReasonSchema = z.enum(API_DENIAL_REASONS);
/** One field-level validation failure, addressed by a dotted path. */
export const FieldErrorSchema = z.object({
    /** e.g. `narrow.contract_type`, or `document.media_type`. */
    path: z.string().min(1).max(200),
    message: z.string().min(1).max(500),
});
export const ApiErrorSchema = z.object({
    code: ApiErrorCodeSchema,
    /**
     * Safe to show a user. It must not vary with whether an out-of-scope resource
     * exists — see the note on `not_found`.
     */
    message: z.string().min(1).max(1000),
    field_errors: z.array(FieldErrorSchema),
    /** Quote this in a support request; it joins the request to its audit rows. */
    correlation_id: CorrelationIdSchema,
    /**
     * Present on every `forbidden` body and on no other — see
     * {@link API_DENIAL_REASONS}.
     *
     * Optional in the schema so each side can ship alone: a front end built
     * against this revision reads a pre-`reason` gateway's `403` as it always did
     * (plain `forbidden`), and an older front end parsing a newer gateway's body
     * strips the key, as `z.object` does with any key it does not know.
     */
    reason: ApiDenialReasonSchema.optional(),
});
/**
 * Discriminated result wrapper, for front-end call sites that would rather
 * branch than catch.
 *
 * The wire format is *not* wrapped: a success is the endpoint's response body at
 * the top level with a 2xx status, and a failure is {@link ApiErrorSchema} with a
 * 4xx/5xx status. This helper is a client-side convenience over that.
 */
export function apiResult(ok) {
    return z.discriminatedUnion('ok', [
        z.object({ ok: z.literal(true), data: ok }),
        z.object({ ok: z.literal(false), error: ApiErrorSchema }),
    ]);
}
//# sourceMappingURL=errors.js.map